import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import {
  UserPlus, Users, MessageSquare, MessageCircle, Send, Mail,
  Trash2, Edit2, X, Save, Zap
} from "lucide-react";
import { toast } from "sonner";
import CallPrompt from "@/pages/trade/CallPrompt";
import ContactAudioRecordButton from "@/pages/trade/ContactAudioRecordButton";
import RecordedCallButton from "@/pages/trade/RecordedCallButton";

const waNumber = (num) => (num || "").replace(/[^0-9]/g, "");
const tgLink = (t) => {
  const v = (t || "").trim();
  if (!v) return "";
  if (v.startsWith("@")) return `https://t.me/${v.slice(1)}`;
  return `https://t.me/+${v.replace(/[^0-9]/g, "")}`;
};

const EMPTY = { name: "", phone: "", whatsapp: "", telegram: "", email: "", city: "", notes: "", save_as: "customer" };

const SAVE_TARGETS = [
  { value: "customer", label: "Customers" },
  { value: "agent", label: "Agents (shipping partners)" },
  { value: "broker", label: "Brokers (sales)" },
  { value: "banker", label: "Bankers (bank permit)" },
  { value: "accountant", label: "Accountant" },
  { value: "gov_helper", label: "Gov Helpers" },
  { value: "important", label: "Important People" },
];

const CustomersPage = () => {
  const { API, authHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickName, setQuickName] = useState("");
  const [quickPhone, setQuickPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [editModal, setEditModal] = useState(null); // customer being edited
  const [form, setForm] = useState(EMPTY);

  const fetchData = () => {
    axios.get(`${API}/trade/customers`, { headers: authHeader() })
      .then(res => setCustomers(res.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  // Fast phone-first save: just name + phone
  const quickSave = async () => {
    if (!quickName.trim() || !quickPhone.trim()) {
      toast.error("Enter the customer name and phone number");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/trade/customers`, {
        ...EMPTY, name: quickName.trim(), phone: quickPhone.trim()
      }, { headers: authHeader() });
      setCustomers(prev => [res.data, ...prev]);
      setQuickName("");
      setQuickPhone("");
      toast.success(`${res.data.name} saved`);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (c) => {
    setEditModal(c);
    setForm({ ...EMPTY, ...c });
  };

  const saveEdit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    const target = form.save_as || "customer";
    try {
      if (target === "customer") {
        await axios.put(`${API}/trade/customers/${editModal.id}`, form, { headers: authHeader() });
        toast.success("Customer updated");
      } else {
        // Save edits first so the move carries the latest details, then move (server removes them from Customers)
        await axios.put(`${API}/trade/customers/${editModal.id}`, form, { headers: authHeader() });
        await axios.post(`${API}/trade/customers/${editModal.id}/move`, { target }, { headers: authHeader() });
        const label = SAVE_TARGETS.find(t => t.value === target)?.label || target;
        toast.success(`Moved to ${label}`);
      }
      setEditModal(null);
      fetchData();
    } catch {
      toast.error("Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try {
      await axios.delete(`${API}/trade/customers/${id}`, { headers: authHeader() });
      toast.success("Customer deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Customers</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">Customers</h1>
          <p className="text-white/50 mt-2">Quick-save a name and phone now — fill in the rest later</p>
        </motion.div>

        {/* Quick add — phone friendly */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="trade-card rounded-sm p-6 mb-8"
          data-testid="quick-add-card"
        >
          <h3 className="text-sm uppercase tracking-widest trade-gold mb-4 flex items-center gap-2">
            <Zap size={14} /> Quick Save
          </h3>
          <div className="flex flex-col md:flex-row gap-3">
            <Input
              value={quickName}
              onChange={e => setQuickName(e.target.value)}
              placeholder="Customer name"
              className="trade-input h-12 rounded-sm flex-1 text-base"
              data-testid="quick-customer-name"
            />
            <Input
              value={quickPhone}
              onChange={e => setQuickPhone(e.target.value)}
              onKeyDown={e => e.key === "Enter" && quickSave()}
              placeholder="Phone number"
              type="tel"
              className="trade-input h-12 rounded-sm flex-1 text-base"
              data-testid="quick-customer-phone"
            />
            <Button
              onClick={quickSave}
              disabled={saving}
              className="trade-btn-primary rounded-sm h-12 px-8 text-base"
              data-testid="quick-save-btn"
            >
              <UserPlus size={18} className="mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </motion.div>

        {/* Customer list */}
        {loading ? (
          <div className="text-center p-16 text-white/40">Loading...</div>
        ) : customers.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            <Users className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No customers yet</h3>
            <p className="text-white/50">Quick-save your first customer above</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {customers.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="trade-card rounded-sm p-5"
                data-testid={`customer-card-${c.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <h3 className="font-serif font-semibold truncate">{c.name}</h3>
                    <p className="text-sm trade-gold font-mono">{c.phone}</p>
                    {c.city && <p className="text-[11px] text-white/40 mt-0.5">{c.city}</p>}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEdit(c)}
                      className="p-2 text-white/50 hover:text-[#D4AF37] transition-colors"
                      data-testid={`edit-customer-${c.id}`}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="p-2 text-red-400/60 hover:text-red-400 transition-colors"
                      data-testid={`delete-customer-${c.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
                  {c.phone && (
                    <CallPrompt
                      phone={c.phone}
                      name={c.name}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                      testid={`call-customer-${c.id}`}
                    />
                  )}
                  <ContactAudioRecordButton
                    phone={c.phone}
                    name={c.name}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                    testid={`audio-customer-${c.id}`}
                  />
                  {c.phone && (
                    <RecordedCallButton
                      phone={c.phone}
                      name={c.name}
                      contactId={c.id}
                      contactType="customer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                      testid={`record-call-customer-${c.id}`}
                    />
                  )}
                  {c.phone && (
                    <a
                      href={`sms:${c.phone}`}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                      data-testid={`text-customer-${c.id}`}
                    >
                      <MessageSquare size={12} /> Text
                    </a>
                  )}
                  {(c.whatsapp || c.phone) && (
                    <a
                      href={`https://wa.me/${waNumber(c.whatsapp || c.phone)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                      data-testid={`whatsapp-customer-${c.id}`}
                    >
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                  )}
                  {c.telegram && (
                    <a
                      href={tgLink(c.telegram)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#24A1DE]/10 text-[#24A1DE] hover:bg-[#24A1DE]/20 transition-colors"
                      data-testid={`telegram-customer-${c.id}`}
                    >
                      <Send size={12} /> Telegram
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Edit modal — fill the rest later */}
        {editModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setEditModal(null)}
            data-testid="customer-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="trade-card rounded-sm p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif font-semibold text-lg">Edit Customer</h3>
                <button
                  onClick={() => setEditModal(null)}
                  className="p-2 text-white/40 hover:text-white transition-colors"
                  data-testid="customer-modal-close"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-white/60">Name *</Label>
                    <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="customer-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Phone *</Label>
                    <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="customer-phone-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">WhatsApp</Label>
                    <Input value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="customer-whatsapp-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Telegram</Label>
                    <Input value={form.telegram} onChange={e => setForm({...form, telegram: e.target.value})} placeholder="@username or phone" className="trade-input mt-1 h-11 rounded-sm" data-testid="customer-telegram-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Email</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="customer-email-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">City</Label>
                    <Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="customer-city-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Notes</Label>
                  <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} placeholder="What they buy, payment habits..." className="trade-input mt-1 rounded-sm" data-testid="customer-notes-input" />
                </div>
                <div className="border border-[#D4AF37]/20 rounded-sm p-4 bg-[#D4AF37]/5">
                  <Label className="text-xs trade-gold uppercase tracking-widest">Save To</Label>
                  <Select value={form.save_as} onValueChange={v => setForm({...form, save_as: v})}>
                    <SelectTrigger className="trade-input mt-2 h-11 rounded-sm" data-testid="customer-save-to-select">
                      <SelectValue placeholder="Choose a list" />
                    </SelectTrigger>
                    <SelectContent>
                      {SAVE_TARGETS.map(t => (
                        <SelectItem key={t.value} value={t.value} data-testid={`save-to-${t.value}`}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {form.save_as !== "customer" && (
                    <p className="text-[11px] text-amber-400/90 mt-2" data-testid="save-to-warning">
                      Saving will move this person to {SAVE_TARGETS.find(t => t.value === form.save_as)?.label} and remove them from Customers.
                    </p>
                  )}
                </div>
                <Button onClick={saveEdit} className="w-full trade-btn-primary rounded-sm h-12" data-testid="save-customer-btn">
                  <Save size={16} className="mr-2" />
                  {form.save_as === "customer"
                    ? "Save Customer"
                    : `Move to ${SAVE_TARGETS.find(t => t.value === form.save_as)?.label}`}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersPage;
